<template>
    <div class="categorized-dropdown-content">
        <!-- Search Input -->
        <div class="el--s-s-dropdown-categorized sm-b-s dw-10">
            <FormSearch v-model="searchQuery"
                :placeholder="searchPlaceholder"
                :debounce="false" />
        </div>

        <div class="el--dropdown-list">
            <!-- Extra Options (if any) -->
            <ul v-if="extraOptions.length && !searchQuery">
                <li v-for="option in extraOptions"
                    :key="option.value"
                    :class="[selectedItem === option.label ? 'el--active' : '', option.disabled === true ? 'el--disabled' : '']"
                    class="el--dropdown-item"
                    @click="onItemSelected(option)">
                    <div>
                        <div>{{ option.label | athT }}</div>
                        <div class="te-subnote" v-if="option.subtitle">{{ option.subtitle | athT }}</div>
                    </div>
                </li>
            </ul>

            <!-- "No matches found" message if no matching items -->
            <div v-if="searchQuery && filteredOptions.length === 0" class="sp-s el--not-found">
                <p>
                    <strong>{{ 'general.labels.no_matches_found' | athT }}</strong>
                </p>
                <p>{{ 'general.labels.check_for_typos' | athT }}</p>
            </div>

            <!-- Categorized Items -->
            <div :class="extraOptions.length ? 'sp-t-m' : 'sp-t-s'">
                <div v-for="(category, index) in filteredOptions" :key="index" class="el--category-group sp-b-m">
                    <div class="f fa-c fj-c sm-b-s">
                        <span class="sm-r-s el--category-title">
                            {{ category.category | athT }}
                        </span>
                        <hr class="el--category-separator" />
                    </div>
                    <ul>
                        <li v-for="item in category.items"
                            :key="item.value"
                            :class="[selectedItem === item.label ? 'el--active' : '', item.disabled === true ? 'el--disabled' : '']"
                            class="el--dropdown-item"
                            @click="onItemSelected(item)">
                            <div>
                                <div>{{ item.label | athT }}</div>
                                <div class="te-subnote" v-if="item.subtitle">{{ item.subtitle | athT }}</div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import FormSearch from '../../atoms/forms/FormSearch.vue';

export default {
    props: {
        /**
         * Unique key identifying the parent dropdown instance that owns this content.
         */
        pubkey: { type: String, default: '' },
        /**
         * Currently selected value's label, used to determine the initially
         * highlighted item.
         */
        value: { type: String, default: null },
        /**
         * Placeholder text or localization key shown in the search input.
         */
        searchPlaceholder: { type: String, default: 'general.search_ellipses' },
        /**
         * Categorized list of options to render, formatted as an array of
         * { category, items: [{ value, label, subtitle }] } objects.
         */
        options: { type: Array, required: true },
        /**
         * Extra options rendered above the categorized list and hidden once the
         * user starts searching.
         */
        extraOptions: { type: Array, default: () => [] },
    },
    components: {FormSearch},
    data() {
        return {
            searchQuery: '',
            selectedItem: this.value || null,
        };
    },
    methods: {
        onItemSelected: function (item) {
            if (item.disabled) return;

            this.$emit('item-selected', item);
        }
    },
    computed: {
        /**
         * Sorted options based on the provided options prop.
         */
        sortedOptions() {
            return this.options.map(option => ({
                category: option.category,
                items: [...option.items].sort((a, b) =>
                    a.label.localeCompare(b.label)
                ),
            }));
        },
        /**
         * Filter sorted options based on the current search query.
         */
        filteredOptions() {
            const query = this.searchQuery.toLowerCase();
            return this.sortedOptions
                .map(category => ({
                    category: category.category,
                    items: category.items.filter(item =>
                        item.label.toLowerCase().includes(query)
                    ),
                }))
                .filter(category => category.items.length > 0);
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/variables';

.el--s-s-dropdown-categorized {
    position: sticky;
    top: 0;
    background: var(--c-panel-bg);
    z-index: 1;
}

.el--active {
    .te-subnote {
        color: var(--c-brand-active-fg);
    }
}

.categorized-dropdown-content {
    min-width: 30rem;
    max-height: 40rem;
    overflow: hidden;
}

.el--dropdown-list {
    overflow: auto;
    max-height: 35rem;
}

.el--category-separator {
    border: none;
    height: 1px;
    background-color: var(--c-panel-border);
    margin: 5px 0;
    width: 100%;
}

.el--category-title {
    font-weight: bold;
    white-space: nowrap;
}

.el--dropdown-item {
    padding: 0.8rem $s-m;
    line-height: $lh-m;
    white-space: normal;
    transition: background-color 0.5s ease;

    .te-subnote {
        text-wrap: auto;
        color: var(--c-txt-light);
        opacity: 0.8;
    }
}

.el--not-found {
    color: var(--c-txt-light);
}

ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

li {
    cursor: pointer;
    letter-spacing: $ls-s;
    border-radius: $br-s;
    color: var(--c-txt-default);
    list-style: none;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        word-wrap: break-word;
    }

    div {
        color: inherit;
    }

    &:not(.el--active):active,
    &:not(.el--active):hover {
        background-color: var(--c-brand-default-bg);
        color: var(--c-brand-default-fg);

        .te-subnote {
            color: var(--c-brand-default-fg);
            opacity: 0.9;
        }
    }

    &.el--active {
        background-color: var(--c-brand-active-bg);
        color: var(--c-brand-active-fg);

        .te-subnote {
            color: var(--c-brand-active-fg);
            opacity: 0.9;
        }
    }

    &.el--disabled {
        cursor: not-allowed;
        filter: grayscale(1);
        opacity: .7;
    }
}
</style>
